import React from 'react'

import { Nullable } from '@f-fertighaus/applications/Next/types/utility'
import { ReviewRating } from '@f-fertighaus/applications/SalesAgentDashboard/interfaces/SADBReview'
import { RatingStar } from '@f-fertighaus/components/ReviewForm/RatingWidget/RatingStar'

export const starColor = {
  1: {
    fill: '#F53158',
    stroke: '#F53158'
  },
  2: {
    fill: '#FFA366',
    stroke: '#FFA366'
  },
  3: {
    fill: '#FFC233',
    stroke: '#FFC233'
  },
  4: {
    fill: '#80E7CC',
    stroke: '#80E7CC'
  },
  5: {
    fill: '#2BD0A4',
    stroke: '#2BD0A4'
  }
}

const ratings = [
  ReviewRating['level-1'],
  ReviewRating['level-2'],
  ReviewRating['level-3'],
  ReviewRating['level-4'],
  ReviewRating['level-5']
]

interface HoverState {
  isHovered: true
  colorSchema: Record<ReviewRating, { stroke: string; fill: string }>
}

interface RatingWidgetProps {
  onChange: (rating: ReviewRating) => void
  value: Nullable<ReviewRating>
  label: string
}

export const RatingWidget = ({ onChange, value, label }: RatingWidgetProps) => {
  const [hoverState, setHoverState] = React.useState<
    { isHovered: false } | HoverState
  >({
    isHovered: false
  })
  const [displayedValue, setDisplayedValue] = React.useState<
    Record<ReviewRating, { stroke: string; fill: string }>
  >(value ? computeStarColors(value) : computeStarColors(null))

  const handleMouseEnter = (rating: ReviewRating) => {
    setHoverState({
      isHovered: true,
      colorSchema: computeStarColors(rating)
    })
  }

  const handleMouseLeave = () => {
    setHoverState({ isHovered: false })
  }

  React.useLayoutEffect(() => {
    setDisplayedValue(computeStarColors(value))
  }, [value])

  return (
    <div className='[ f-d-flc ] reputation-rating-widget'>
      <div className='[ f-t-bold f-t-fancy f-m-b-010 ]'>{label}</div>
      <div className='[ f-d-flr f-fl-jc-sb ] reputation-rating-container'>
        {ratings.map(rating => (
          <RatingStar
            key={rating}
            dataTestId={`rating-${rating}`}
            onClick={() => onChange(rating)}
            onMouseEnter={() => handleMouseEnter(rating)}
            onMouseLeave={handleMouseLeave}
            color={
              hoverState.isHovered
                ? hoverState.colorSchema[rating]
                : displayedValue[rating]
            }
          />
        ))}
      </div>
    </div>
  )
}

export const computeStarColors = (
  rating: Nullable<ReviewRating>,
  noBorders?: boolean
) =>
  rating
    ? Array.from(Array(5).keys()).reduce<
        Record<ReviewRating, { stroke: string; fill: string }>
      >((acc, _, currentIndex) => {
        const indexAligned = currentIndex + 1
        return indexAligned > rating
          ? {
              ...acc,
              [indexAligned]: {
                stroke: '#D2D4D9',
                fill: noBorders ? '#D2D4D9' : '#F0F1F2'
              }
            }
          : {
              ...acc,
              [indexAligned]: {
                stroke: starColor[rating].stroke,
                fill: starColor[rating].fill
              }
            }
      }, {})
    : Array.from(Array(5).keys()).reduce(
        (acc, _, currentIndex) => ({
          ...acc,
          [currentIndex + 1]: {
            stroke: '#D2D4D9',
            fill: noBorders ? '#D2D4D9' : '#F0F1F2'
          }
        }),
        {}
      )

export const NO_RATING_COLOR = '#D2D4D9'
